import { useState, useCallback } from 'react'
import type { UserLocation, LocationPermission } from '@/types'

interface UseGeolocationReturn {
  location: UserLocation | null
  permission: LocationPermission
  loading: boolean
  error: string | null
  requestLocation: () => Promise<UserLocation | null>
}

export function useGeolocation(): UseGeolocationReturn {
  const [location, setLocation] = useState<UserLocation | null>(null)
  const [permission, setPermission] = useState<LocationPermission>('prompt')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const requestLocation = useCallback(async (): Promise<UserLocation | null> => {
    if (!navigator.geolocation) {
      setPermission('unavailable')
      setError('Geolocation is not supported by your browser')
      return null
    }

    setLoading(true)
    setError(null)

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation: UserLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          }
          setLocation(userLocation)
          setPermission('granted')
          setLoading(false)
          resolve(userLocation)
        },
        (err) => {
          setLoading(false)
          switch (err.code) {
            case err.PERMISSION_DENIED:
              setPermission('denied')
              setError('Location permission denied')
              break
            case err.POSITION_UNAVAILABLE:
              setPermission('unavailable')
              setError('Location information unavailable')
              break
            case err.TIMEOUT:
              setError('Location request timed out')
              break
            default:
              setError('An unknown error occurred')
          }
          resolve(null)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 30000,
        }
      )
    })
  }, [])

  return {
    location,
    permission,
    loading,
    error,
    requestLocation,
  }
}
